import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { CopyCheck, Merge, ChevronRight, RefreshCw, Loader2, Layers } from "lucide-react";
import api from "../../lib/axios";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { formatNumber } from "@/utils/formatters";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const DuplicateGroupCard = ({ group, checked, onToggle, onMerge }) => (
  <div
    className={`rounded-lg border p-3 transition-colors ${checked ? "border-blue-400 bg-blue-50/40" : "border-slate-200"}`}
    data-testid={`duplicate-group-${group.key}`}
  >
    <div className="flex items-center justify-between gap-3 mb-2">
      <div className="flex items-center gap-3 min-w-0">
        <Checkbox checked={checked} onCheckedChange={onToggle} data-testid={`select-group-${group.key}`} />
        <div className="min-w-0">
          <p className="font-medium text-slate-900 truncate">{group.name}</p>
          <p className="text-xs text-slate-500 flex items-center gap-1 flex-wrap">
            {[group.material, group.series, group.color, group.factory_name].filter(Boolean).join(" · ") || "Sin material/serie"}
            <Badge variant="secondary" className="text-xs">{group.count} tipos</Badge>
            <span className="text-slate-400">· destino: el de más muestras ({group.types[0]?.entries_count || 0})</span>
          </p>
        </div>
      </div>
      <Button size="sm" onClick={onMerge} className="bg-blue-600 hover:bg-blue-700 shrink-0" data-testid={`merge-group-btn-${group.key}`}>
        <Merge className="w-4 h-4 mr-2" /> Fusionar
      </Button>
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
      {group.types.slice(0, 12).map((t) => (
        <div key={t.id} className="flex items-center gap-2 text-sm bg-slate-50 rounded px-2 py-1.5">
          <div className="flex-1 min-w-0">
            <p className="truncate text-xs text-slate-600">{[t.color, t.factory_name].filter(Boolean).join(" · ") || "—"}</p>
            <p className="text-xs text-slate-500">{t.entries_count || 0} muestras</p>
          </div>
          <span className="font-mono text-xs text-emerald-700">${formatNumber(t.average_cost_per_m2 || 0)}/m²</span>
        </div>
      ))}
      {group.types.length > 12 && <p className="text-xs text-slate-500 px-2 self-center">… y {group.types.length - 12} más</p>}
    </div>
  </div>
);

export const FactoryDuplicatesAlert = ({
  duplicates, isLoading, options, onOptionsChange, onRefresh, onMergeGroup, factoryPassword, onBatchMerged,
}) => {
  const [open, setOpen] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState(new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [isMerging, setIsMerging] = useState(false);
  const totalGroups = duplicates?.total_groups || 0;
  const groups = duplicates?.groups || [];

  useEffect(() => { setSelectedKeys(new Set()); }, [duplicates]);

  if (!isLoading && totalGroups === 0) return null;

  const selectedGroups = groups.filter((g) => selectedKeys.has(g.key));
  const selectedTypesCount = selectedGroups.reduce((s, g) => s + g.count, 0);

  const toggleGroup = (key) => setSelectedKeys((prev) => {
    const next = new Set(prev);
    next.has(key) ? next.delete(key) : next.add(key);
    return next;
  });

  const toggleAll = () => setSelectedKeys(selectedKeys.size === groups.length ? new Set() : new Set(groups.map((g) => g.key)));

  const handleBatchMerge = async () => {
    setIsMerging(true);
    try {
      const res = await api.post(
        `${API}/factory/merge-groups`,
        { groups: selectedGroups.map((g) => ({ type_ids: g.types.map((t) => t.id) })) },
        { headers: { "X-Factory-Password": factoryPassword } }
      );
      toast.success(`${res.data.groups_merged} grupos fusionados · ${res.data.merged} tipos eliminados, ${res.data.entries_moved} muestras movidas`);
      if (res.data.errors?.length) toast.warning(`${res.data.errors.length} grupo(s) no se pudieron fusionar`);
      setConfirmOpen(false);
      setOpen(false);
      onBatchMerged?.({
        merge_id: res.data.batch_id,
        merged: res.data.merged,
        target_name: `${res.data.groups_merged} grupos`,
        undo_expires_at: res.data.undo_expires_at,
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al fusionar grupos");
    } finally {
      setIsMerging(false);
    }
  };

  return (
    <>
      <div className="flex items-center justify-between gap-3 px-4 py-3 rounded-lg border border-amber-200 bg-amber-50 text-amber-900" data-testid="factory-duplicates-alert">
        <div className="flex items-center gap-3 text-sm">
          <CopyCheck className="w-4 h-4 text-amber-600 shrink-0" />
          {isLoading ? (
            <span className="flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" /> Buscando posibles duplicados…</span>
          ) : (
            <span>
              Se detectaron <strong data-testid="duplicates-count">{totalGroups}</strong> grupo{totalGroups === 1 ? "" : "s"} de tipos que parecen iguales
              <span className="text-amber-700"> (mismo nombre, material y serie{options.byColor ? ", color" : ""}{options.byFactory ? ", fábrica" : ""})</span>.
            </span>
          )}
        </div>
        <Button size="sm" variant="outline" onClick={() => setOpen(true)} disabled={isLoading || totalGroups === 0} className="border-amber-300 text-amber-800 hover:bg-amber-100" data-testid="review-duplicates-btn">
          Revisar <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl" data-testid="factory-duplicates-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between gap-2">
              <span className="flex items-center gap-2"><CopyCheck className="w-5 h-5 text-amber-600" /> Posibles duplicados ({totalGroups})</span>
              <Button size="sm" variant="ghost" onClick={onRefresh} disabled={isLoading} data-testid="refresh-duplicates-btn">
                <RefreshCw className={`w-4 h-4 ${isLoading ? "animate-spin" : ""}`} />
              </Button>
            </DialogTitle>
            <DialogDescription>
              Pulsa "Fusionar" en un grupo para elegir el destino, o marca varios y fusiónalos de una vez (el destino será el tipo con más muestras).
              {groups.length < totalGroups && ` Mostrando los ${groups.length} grupos más grandes de ${totalGroups}.`}
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-wrap items-center justify-between gap-3 px-1 py-2 border-y border-slate-100 text-sm">
            <div className="flex items-center gap-4">
              <span className="text-slate-500 flex items-center gap-1"><Layers className="w-4 h-4" /> Agrupar también por:</span>
              <div className="flex items-center gap-2">
                <Checkbox id="dup-by-color" checked={options.byColor} onCheckedChange={(v) => onOptionsChange({ ...options, byColor: !!v })} data-testid="dup-by-color" />
                <Label htmlFor="dup-by-color" className="cursor-pointer">Color</Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox id="dup-by-factory" checked={options.byFactory} onCheckedChange={(v) => onOptionsChange({ ...options, byFactory: !!v })} data-testid="dup-by-factory" />
                <Label htmlFor="dup-by-factory" className="cursor-pointer">Fábrica</Label>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox id="dup-select-all" checked={groups.length > 0 && selectedKeys.size === groups.length} onCheckedChange={toggleAll} data-testid="dup-select-all" />
              <Label htmlFor="dup-select-all" className="cursor-pointer text-slate-600">Seleccionar todos</Label>
            </div>
          </div>

          <div className="max-h-[50vh] overflow-y-auto space-y-3 pr-1">
            {isLoading ? (
              <div className="flex items-center justify-center py-10 text-slate-500"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Recalculando grupos…</div>
            ) : groups.map((group) => (
              <DuplicateGroupCard
                key={group.key}
                group={group}
                checked={selectedKeys.has(group.key)}
                onToggle={() => toggleGroup(group.key)}
                onMerge={() => { setOpen(false); onMergeGroup(group.types); }}
              />
            ))}
          </div>

          <DialogFooter className="sm:justify-between items-center">
            <span className="text-sm text-slate-500" data-testid="selected-groups-summary">
              {selectedGroups.length > 0 ? `${selectedGroups.length} grupo${selectedGroups.length === 1 ? "" : "s"} · ${selectedTypesCount} tipos → ${selectedTypesCount - selectedGroups.length} se eliminarán` : "Ningún grupo seleccionado"}
            </span>
            <Button onClick={() => setConfirmOpen(true)} disabled={selectedGroups.length === 0 || isMerging} className="bg-indigo-600 hover:bg-indigo-700" data-testid="merge-selected-groups-btn">
              <Merge className="w-4 h-4 mr-2" /> Fusionar {selectedGroups.length || ""} grupo{selectedGroups.length === 1 ? "" : "s"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="confirm-batch-merge-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Fusionar {selectedGroups.length} grupos?</AlertDialogTitle>
            <AlertDialogDescription>
              En cada grupo, el tipo con más muestras recibirá las muestras de los demás y estos serán eliminados
              ({selectedTypesCount - selectedGroups.length} tipos en total). Podrás deshacerlo durante 10 minutos.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isMerging} data-testid="confirm-batch-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); handleBatchMerge(); }} disabled={isMerging} className="bg-indigo-600 hover:bg-indigo-700" data-testid="confirm-batch-merge-btn">
              {isMerging ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Merge className="w-4 h-4 mr-2" />}
              Sí, fusionar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default FactoryDuplicatesAlert;
